import React, { useMemo, useContext, useEffect } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { makeStyles } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyDateSetting, validateDate, getCurrentDate } from '@bingads-webui-campaign/date-setting';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';

import { DateManagementSelector } from './date-management-selector';
import { DateManagementInfo } from './date-management-info';
import { BudgetTypes } from '../../../../common/constants';
import { validateLifetimeBudgetDateRange } from './utils';

const useStyles = makeStyles({
  dateManagementSelector: {
    marginBottom: '16px',
  },
});

export const AudienceDateSetting = ({
  validationProps,
  updateValidation,
  isEditFlow,
  displayDateManagementSelector,
  allowDateManagementSelection,
  ...restProps
}) => {
  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);
  const classes = useStyles();

  const { data } = validationProps;

  const {
    StartDate,
    EndDate,
    UseCampaignLevelDates,
    BudgetType,
  } = data;

  const currentDate = getCurrentDate();

  const validateLifetimeBudgetDateSettings = (newData, errors) => {
    if (BudgetType === BudgetTypes.LifetimeBudgetStandard) {
      const budgetRangeError = validateLifetimeBudgetDateRange(newData, i18n);

      if (budgetRangeError) {
        errors.push(budgetRangeError);
      }
    }
  };

  const validate = (newData) => {
    const validationError = validateDate(newData, i18n);

    validateLifetimeBudgetDateSettings(newData, validationError);

    updateValidation(validationError, newData);
  };

  const logActivity = (action, newData) => {
    if (activity) {
      activity.signal(`${action} - ${JSON.stringify(newData)}`);
    }
  };

  useEffect(() => {
    const validationErrors = [];

    validateLifetimeBudgetDateSettings(data, validationErrors);

    updateValidation(validationErrors);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BudgetType]);


  const onDateChange = (newData) => {
    const amendedData = {
      ...newData,
      UseCampaignLevelDates,
    };

    validate(amendedData);

    logActivity('onLegacyDateSettingChange', amendedData);
  };

  const onDateManagementChange = (newData) => {
    const amendedData = {
      ...newData,
      StartDate,
      EndDate,
    };

    validate(amendedData);

    logActivity('onDateManagementChange', amendedData);
  };

  const dateSettingWithDefaultStartDate = useMemo(() => _.defaults(data, {
    StartDate: currentDate,
  }), [currentDate, data]);

  return (
    <div>
      {displayDateManagementSelector &&
        <div className={classes.dateManagementSelector}>
          <DateManagementSelector
            onChange={onDateManagementChange}
            value={UseCampaignLevelDates}
            isEnabled={allowDateManagementSelection}
          />
        </div>
      }
      {(UseCampaignLevelDates || !isEditFlow) ?
        <LegacyDateSetting
          onChange={onDateChange}
          data={dateSettingWithDefaultStartDate}
          activityLogger={activity}
          {...restProps}
        /> :
        <DateManagementInfo />
      }
    </div>
  );
};

AudienceDateSetting.propTypes = {
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      StartDate: PropTypes.instanceOf(Date),
      EndDate: PropTypes.instanceOf(Date),
      UseCampaignLevelDates: PropTypes.bool,
      BudgetType: PropTypes.string,
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  displayDateManagementSelector: PropTypes.bool,
  allowDateManagementSelection: PropTypes.bool,
  isEditFlow: PropTypes.bool,
};

AudienceDateSetting.defaultProps = {
  displayDateManagementSelector: false,
  allowDateManagementSelection: true,
  isEditFlow: false,
};

export const ValidatedAudienceDateSetting = withError(withCustomValidation(AudienceDateSetting));
